import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Spin,
  Alert,
  Table,
  Space
} from 'antd';
import {
  RiseOutlined,
  UserOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  DollarOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { adminService } from '../../services/api';

const { Title } = Typography;

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsData, monthlyStats, topUsersData] = await Promise.all([
        adminService.getStats(),
        adminService.getMonthlyStats(),
        adminService.getTopUsers()
      ]);
      setStats(statsData);
      setMonthlyData(monthlyStats);
      setTopUsers(topUsersData);
    } catch (error) {
      setError('Error al cargar las estadísticas');
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthlyColumns = [
    {
      title: 'Mes',
      dataIndex: 'month',
      key: 'month',
    },
    {
      title: 'Usuarios Nuevos',
      dataIndex: 'newUsers',
      key: 'newUsers',
    },
    {
      title: 'Facturas',
      dataIndex: 'invoices',
      key: 'invoices',
    },
    {
      title: 'Créditos',
      dataIndex: 'credits',
      key: 'credits',
    },
    {
      title: 'Ingresos',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value) => `$${value}`,
    },
  ];

  const topUsersColumns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Facturas',
      dataIndex: 'invoiceCount',
      key: 'invoiceCount',
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Space align="center" style={{ marginBottom: 24 }}>
        <BarChartOutlined style={{ fontSize: 24 }} />
        <Title level={2} style={{ margin: 0 }}>
          Estadísticas y Reportes
        </Title>
      </Space>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Estadísticas generales */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Usuarios"
              value={stats?.totalUsers || 0}
              prefix={<UserOutlined />}
              suffix={
                <div style={{ fontSize: 12, color: '#52c41a' }}>
                  +{stats?.newUsersThisMonth || 0} este mes
                </div>
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Facturas Totales"
              value={stats?.totalInvoices || 0}
              prefix={<FileTextOutlined />}
              suffix={
                <div style={{ fontSize: 12, color: '#52c41a' }}>
                  +{stats?.invoicesThisMonth || 0} este mes
                </div>
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Créditos Consumidos"
              value={stats?.totalCreditsConsumed || 0}
              prefix={<CreditCardOutlined />}
              suffix={
                <div style={{ fontSize: 12, color: '#1890ff' }}>
                  {stats?.creditsThisMonth || 0} este mes
                </div>
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ingresos Totales"
              value={stats?.totalRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              suffix={
                <div style={{ fontSize: 12, color: '#52c41a' }}>
                  ${stats?.revenueThisMonth || 0} este mes
                </div>
              }
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Estadísticas mensuales */}
        <Col xs={24} lg={16}>
          <Card title="Estadísticas Mensuales">
            <Table
              columns={monthlyColumns}
              dataSource={monthlyData.map((item, index) => ({ ...item, key: index }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Top usuarios */}
        <Col xs={24} lg={8}>
          <Card 
            title={
              <Space>
                <RiseOutlined />
                Top Usuarios
              </Space>
            }
          >
            <Table
              columns={topUsersColumns}
              dataSource={topUsers.map((item, index) => ({ ...item, key: index }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminStats;